import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { TSuggest } from 'feature/registration/types'
import { v4 as uuidv4 } from 'uuid'

import { orderApiSpace } from '../namespaces/orderApiSpace'
import { orderFormSpace } from '../namespaces/orderFormSpace'

const FORM_PREFIX = 'order'
export const CURRENT_ORDER_PRODUCT = `${FORM_PREFIX}_currentEditOrderForm`
export const CURRENT_ORDER_CONSULT = `${FORM_PREFIX}_currentEditConsultForm`
export const CURRENT_SEND_FORM = `${FORM_PREFIX}_currentSendForm`

// Функция для сохранения данных формы в localStorage (заявки продукции)
const saveFormToLocalStorage = (
  formType: orderFormSpace.FormType,
  formData: orderFormSpace.FormsType,
): void => {
  localStorage.setItem(`${FORM_PREFIX}_${formType}`, JSON.stringify(formData))
}
// Функция для сохранения данных формы в localStorage (заявки консультации)
const saveConsultationFormToLocalStorage = (
  formType: orderFormSpace.ConsultationFormType,
  formData: orderFormSpace.ConsultationsFormType,
): void => {
  localStorage.setItem(`${FORM_PREFIX}_${formType}`, JSON.stringify(formData))
}

// Функция для загрузки данных формы из localStorage (заявки продукции)
const loadOrderFormFromLocalStorage = (
  formType: orderFormSpace.FormType,
): orderFormSpace.FormsType | null => {
  const formData = localStorage.getItem(`${FORM_PREFIX}_${formType}`)
  return formData ? JSON.parse(formData) : null
}
// Функция для загрузки данных формы из localStorage (заявки консультации)
const loadConsultFormFromLocalStorage = (
  formType: orderFormSpace.ConsultationFormType,
): orderFormSpace.ConsultationsFormType | null => {
  const formData = localStorage.getItem(`${FORM_PREFIX}_${formType}`)
  return formData ? JSON.parse(formData) : null
}

// Функция для получения значений по умолчанию для формы
const getDefaultFormData = (
  formType: orderFormSpace.FormType,
): orderFormSpace.FormsType => {
  return {
    type: formType,
    activeStep: 0,
    products: [],
    params: [],
    images: [],
    address: null,
    timeSlots: [
      {
        key: uuidv4(),
        id: 0,
        value: '',
        date: '',
        isValidDate: false,
      },
    ],
    timeSlotsRegionFiasId: null,
    dealerCode: '',
    phone: '',
    email: '',
    userType: null,
    bankDetailsId: null,
  }
}

// Функция для получения значений по умолчанию для формы консультации
const getDefaultConsultationFormData = (
  formType: orderFormSpace.ConsultationFormType,
): orderFormSpace.ConsultationsFormType => {
  return {
    type: formType,
    comment: '',
    products: null,
    images: [],
  }
}

// получаем текущую форму для заявки
const getCurrentEditOrderForm = () => {
  const data = localStorage.getItem(CURRENT_ORDER_PRODUCT)
  return data
    ? (JSON.parse(data) as orderFormSpace.FormType)
    : orderFormSpace.FormType.new
}

// получаем текущую форму для консультации
const getCurrentEditConsultForm = () => {
  const data = localStorage.getItem(CURRENT_ORDER_CONSULT)
  return data
    ? (JSON.parse(data) as orderFormSpace.ConsultationFormType)
    : orderFormSpace.ConsultationFormType.consultation
}

// получаем текущую форму отправки
// (та форма которая последняя была подготовлена к отправке)
const getCurrentSendForm = () => {
  const data = localStorage.getItem(CURRENT_SEND_FORM)
  if (data) {
    const suggest = JSON.parse(data)

    if (suggest in orderFormSpace.FormType)
      return suggest as orderFormSpace.FormType
    if (suggest in orderFormSpace.ConsultationFormType)
      return suggest as orderFormSpace.ConsultationFormType
  }

  return orderFormSpace.FormType.new
}

const initialState: orderFormSpace.State = {
  forms: {
    [orderFormSpace.FormType.new]:
      loadOrderFormFromLocalStorage(orderFormSpace.FormType.new) ||
      getDefaultFormData(orderFormSpace.FormType.new),
    [orderFormSpace.FormType.service]:
      loadOrderFormFromLocalStorage(orderFormSpace.FormType.service) ||
      getDefaultFormData(orderFormSpace.FormType.service),
    [orderFormSpace.FormType.newCompany]:
      loadOrderFormFromLocalStorage(orderFormSpace.FormType.newCompany) ||
      getDefaultFormData(orderFormSpace.FormType.newCompany),
  },
  consultationForms: {
    [orderFormSpace.ConsultationFormType.consultation]:
      loadConsultFormFromLocalStorage(
        orderFormSpace.ConsultationFormType.consultation,
      ) ||
      getDefaultConsultationFormData(
        orderFormSpace.ConsultationFormType.consultation,
      ),
    [orderFormSpace.ConsultationFormType.consultationProduct]:
      loadConsultFormFromLocalStorage(
        orderFormSpace.ConsultationFormType.consultationProduct,
      ) ||
      getDefaultConsultationFormData(
        orderFormSpace.ConsultationFormType.consultationProduct,
      ),
  },
  currentEditForm: getCurrentEditOrderForm(),
  currentEditConsultForm: getCurrentEditConsultForm(),
  currentSendForm: getCurrentSendForm(),
}

// используется для работы с данными заявок
export const orderFormSlice = createSlice({
  name: 'orderForm',
  initialState,
  reducers: {
    setCurrentEditForm: (
      state,
      action: PayloadAction<orderFormSpace.FormType>,
    ) => {
      state.currentEditForm = action.payload
      state.currentSendForm = action.payload
      localStorage.setItem(
        CURRENT_ORDER_PRODUCT,
        JSON.stringify(action.payload),
      )
      localStorage.setItem(CURRENT_SEND_FORM, JSON.stringify(action.payload))
    },
    // установить текущую форму на дефолтные данные
    // делаем после отправки формы
    setDefaultFormData: (state, _action: PayloadAction<void>) => {
      state.forms[state.currentEditForm] = getDefaultFormData(
        state.currentEditForm,
      )
      const currentForm = state.forms[state.currentEditForm]
      saveFormToLocalStorage(state.currentEditForm, currentForm)
    },
    // установить все формы на дефолтные данные
    // нужно для logout
    resetOrderForms: (state, _action: PayloadAction<void>) => {
      let form

      // order new
      state.forms[orderFormSpace.FormType.new] = getDefaultFormData(
        orderFormSpace.FormType.new,
      )
      form = state.forms[orderFormSpace.FormType.new]
      saveFormToLocalStorage(orderFormSpace.FormType.new, form)

      // order service
      state.forms[orderFormSpace.FormType.service] = getDefaultFormData(
        orderFormSpace.FormType.service,
      )
      form = state.forms[orderFormSpace.FormType.service]
      saveFormToLocalStorage(orderFormSpace.FormType.service, form)

      // order newCompany
      state.forms[orderFormSpace.FormType.newCompany] = getDefaultFormData(
        orderFormSpace.FormType.newCompany,
      )
      form = state.forms[orderFormSpace.FormType.newCompany]
      saveFormToLocalStorage(orderFormSpace.FormType.newCompany, form)

      // order consultation
      state.consultationForms[
        orderFormSpace.ConsultationFormType.consultation
      ] = getDefaultConsultationFormData(
        orderFormSpace.ConsultationFormType.consultation,
      )
      form = state.consultationForms.consultation
      saveConsultationFormToLocalStorage(
        orderFormSpace.ConsultationFormType.consultation,
        form,
      )

      // order consultation-product
      state.consultationForms[
        orderFormSpace.ConsultationFormType.consultationProduct
      ] = getDefaultConsultationFormData(
        orderFormSpace.ConsultationFormType.consultationProduct,
      )
      form = state.consultationForms.consultation
      saveConsultationFormToLocalStorage(
        orderFormSpace.ConsultationFormType.consultationProduct,
        form,
      )
    },
    setActiveStepOrder(state, action: PayloadAction<number>) {
      const currentForm = state.forms[state.currentEditForm]

      currentForm.activeStep = action.payload
      saveFormToLocalStorage(state.currentEditForm, currentForm)
    },
    // переключить активность продукта
    // если он не активен удалить его параметры и изображения
    toggleProductOrder: (
      state,
      action: PayloadAction<orderFormSpace.Product>,
    ) => {
      const currentForm = state.forms[state.currentEditForm]

      if (currentForm.products?.some(item => item.id === action.payload.id)) {
        currentForm.products = currentForm.products.filter(
          item => item.id !== action.payload.id,
        )
        currentForm.params = currentForm.params.filter(
          item => item.productId !== action.payload.id,
        )
        currentForm.images = [
          ...currentForm.images.filter(
            item => item.productId !== action.payload.id,
          ),
        ]
      } else {
        currentForm.products?.push(action.payload)
      }

      saveFormToLocalStorage(state.currentEditForm, currentForm)
    },
    // удалить продукт
    // удаляем его параметры и изображения
    removeProductOrder: (state, action: PayloadAction<number>) => {
      const currentForm = state.forms[state.currentEditForm]

      currentForm.products = currentForm.products?.filter(
        item => item.id !== action.payload,
      ) as orderFormSpace.Product[]
      currentForm.params.forEach(cur => {
        if (cur.type === 'IMAGES') {
          currentForm.images = [
            ...currentForm.images.filter(
              item => item.productId !== action.payload,
            ),
          ]
        }
      })
      currentForm.params = currentForm.params.filter(
        item => item.productId !== action.payload,
      )
      currentForm.images = currentForm.images.filter(
        item => item.productId !== action.payload,
      )

      saveFormToLocalStorage(state.currentEditForm, currentForm)
    },
    // очистить данные заказа: продукты, параметры, изображения, адрес
    clearProductsOrder: state => {
      const currentForm = state.forms[state.currentEditForm]
      currentForm.products = []
      currentForm.params = []
      currentForm.images = []
      currentForm.address = null
      saveFormToLocalStorage(state.currentEditForm, currentForm)
    },
    // инициализация параметров (перевод данных в другой формат для работы)
    initParamsOrder: (
      state,
      action: PayloadAction<orderApiSpace.ProductParamList>,
    ) => {
      const currentForm = state.forms[state.currentEditForm]
      const newParams: orderFormSpace.Parameter[] = []

      action.payload.list.forEach(product => {
        if (product.parameters) {
          product.parameters?.forEach(param => {
            const storedParamIndex = currentForm.params.findIndex(
              elem => elem.id === param.id,
            )
            if (storedParamIndex !== -1) {
              newParams.push({
                id: param.id,
                name: param.name,
                iconUrl: param.iconUrl,
                type: param.typeCode,
                productId: product.productId,
                value: currentForm.params[storedParamIndex].value,
              })
            } else
              newParams.push({
                id: param.id,
                name: param.name,
                iconUrl: param.iconUrl,
                type: param.typeCode,
                productId: product.productId,
                value: param.typeCode === 'CHECKBOX' ? false : null,
              })
          })
        }
      })
      currentForm.params = newParams

      saveFormToLocalStorage(state.currentEditForm, currentForm)
    },
    setParamOrder: (state, action: PayloadAction<orderFormSpace.Parameter>) => {
      const currentForm = state.forms[state.currentEditForm]

      if (currentForm.params === null) currentForm.params = []
      currentForm.params = currentForm.params?.filter(
        item => item.id !== action.payload.id,
      )
      currentForm.params.push(action.payload)

      saveFormToLocalStorage(state.currentEditForm, currentForm)
    },
    // установить текущее значение изображений для параметра
    setParamImagesOrder: (
      state,
      action: PayloadAction<orderFormSpace.Images[]>,
    ) => {
      const currentForm = state.forms[state.currentEditForm]

      currentForm.images = [
        ...currentForm.images.filter(
          item => item.paramId !== action.payload[0].paramId,
        ),
        ...action.payload,
      ]

      saveFormToLocalStorage(state.currentEditForm, currentForm)
    },
    // удалить изображения для параметра
    removeParamImagesOrder: (state, action: PayloadAction<number>) => {
      const currentForm = state.forms[state.currentEditForm]

      currentForm.images = [
        ...currentForm.images.filter(item => item.paramId !== action.payload),
      ]

      saveFormToLocalStorage(state.currentEditForm, currentForm)
    },
    // установить адрес
    setAddressOrder: (state, action: PayloadAction<TSuggest | null>) => {
      const currentForm = state.forms[state.currentEditForm]

      currentForm.address = action.payload

      saveFormToLocalStorage(state.currentEditForm, currentForm)
    },
    // добавить тайм слот
    addTimeSlotOrder: (state, _actions: PayloadAction<void>) => {
      const currentForm = state.forms[state.currentEditForm]

      currentForm.timeSlots.push({
        key: uuidv4(),
        id: 0,
        value: '',
        date: '',
      })

      saveFormToLocalStorage(state.currentEditForm, currentForm)
    },
    // установить timeSlotsRegionFiasId
    setTimeSlotsRegionFiasId: (state, actions: PayloadAction<string>) => {
      const currentForm = state.forms[state.currentEditForm]

      currentForm.timeSlotsRegionFiasId = actions.payload
      saveFormToLocalStorage(state.currentEditForm, currentForm)
    },
    // почистить тайм слоты
    clearTimeSlotOrder: (state, _actions: PayloadAction<void>) => {
      const currentForm = state.forms[state.currentEditForm]

      currentForm.timeSlots = [
        {
          key: uuidv4(),
          id: 0,
          value: '',
          date: '',
        },
      ]

      saveFormToLocalStorage(state.currentEditForm, currentForm)
    },
    // удалить тайм слот
    removeTimeSlotOrder: (state, actions: PayloadAction<string>) => {
      const currentForm = state.forms[state.currentEditForm]

      currentForm.timeSlots = currentForm.timeSlots.filter(
        item => item.key !== actions.payload,
      )

      saveFormToLocalStorage(state.currentEditForm, currentForm)
    },
    // обновить тайм слот
    updateTimeSlotOrder: (
      state,
      actions: PayloadAction<orderFormSpace.TimeSlot>,
    ) => {
      const currentForm = state.forms[state.currentEditForm]

      const index = currentForm.timeSlots.findIndex(
        item => item.key === actions.payload.key,
      )
      if (actions.payload.value !== undefined) {
        currentForm.timeSlots[index] = {
          ...currentForm.timeSlots[index],
          ...actions.payload,
          date: currentForm.timeSlots[index].date,
        }
      } else {
        currentForm.timeSlots[index] = {
          ...currentForm.timeSlots[index],
          ...actions.payload,
          value: currentForm.timeSlots[index].value,
        }
      }

      saveFormToLocalStorage(state.currentEditForm, currentForm)
    },
    // установить код дилера
    setDialerCodeOrder: (state, actions: PayloadAction<string>) => {
      const currentForm = state.forms[state.currentEditForm]

      currentForm.dealerCode = actions.payload

      saveFormToLocalStorage(state.currentEditForm, currentForm)
    },
    // установить номер телефона
    setPhoneOrder: (state, actions: PayloadAction<string>) => {
      const currentForm = state.forms[state.currentEditForm]

      currentForm.phone = actions.payload

      saveFormToLocalStorage(state.currentEditForm, {
        ...currentForm,
        phone: actions.payload.replace(/[^0-9+]/g, ''),
      })
    },
    // установить email
    setEmailOrder: (state, actions: PayloadAction<string>) => {
      const currentForm = state.forms[state.currentEditForm]

      currentForm.email = actions.payload

      saveFormToLocalStorage(state.currentEditForm, currentForm)
    },
    // установить тип пользователя (ФЛ/ЮЛ)
    setUserTypeOrder: (
      state,
      actions: PayloadAction<orderFormSpace.UserType>,
    ) => {
      const currentForm = state.forms[state.currentEditForm]

      currentForm.userType = actions.payload

      saveFormToLocalStorage(state.currentEditForm, currentForm)
    },
    // установить id банковских реквизитов
    setBankDetailsIdOrder: (state, actions: PayloadAction<number | null>) => {
      const currentForm = state.forms[state.currentEditForm]

      currentForm.bankDetailsId = actions.payload

      saveFormToLocalStorage(state.currentEditForm, currentForm)
    },
    //consultation form method
    setCurrentEditConsultForm: (
      state,
      action: PayloadAction<orderFormSpace.ConsultationFormType>,
    ) => {
      state.currentEditConsultForm = action.payload
      state.currentSendForm = action.payload
      localStorage.setItem(
        CURRENT_ORDER_CONSULT,
        JSON.stringify(action.payload),
      )
      localStorage.setItem(CURRENT_SEND_FORM, JSON.stringify(action.payload))
    },
    setConsultationComment: (state, actions: PayloadAction<string>) => {
      const currentForm = state.consultationForms[state.currentEditConsultForm]
      currentForm.comment = actions.payload
      saveConsultationFormToLocalStorage(
        state.currentEditConsultForm,
        currentForm,
      )
    },
    setConsultationImages: (
      state,
      action: PayloadAction<orderFormSpace.Images[]>,
    ) => {
      const currentForm = state.consultationForms[state.currentEditConsultForm]
      // currentForm.images = action.payload.map(item => ({
      //   uid: item.serverUid,
      //   previewUrl: item.previewUrl,
      // }))
      currentForm.images = action.payload

      saveConsultationFormToLocalStorage(
        state.currentEditConsultForm,
        currentForm,
      )
    },
    setDefaultConsultFormData: (state, _action: PayloadAction<void>) => {
      state.consultationForms[state.currentEditConsultForm] =
        getDefaultConsultationFormData(state.currentEditConsultForm)
      const currentForm = state.consultationForms[state.currentEditConsultForm]
      saveConsultationFormToLocalStorage(
        state.currentEditConsultForm,
        currentForm,
      )
    },
    setConsultationProducts: (
      state,
      action: PayloadAction<orderFormSpace.Product[]>,
    ) => {
      const currentForm = state.consultationForms[state.currentEditConsultForm]
      state.consultationForms.consultationProduct.products = action.payload
      saveConsultationFormToLocalStorage(
        state.currentEditConsultForm,
        currentForm,
      )
    },
  },
})

export const { actions, reducer } = orderFormSlice
export const { actions: orderFormActions } = orderFormSlice

// TODO переменность формы
// TODO вынести в отдельный файл работу с localStorage
